import {Alert, Button, Modal} from 'antd';
import {useMemo, useState} from 'react';
import {generatePath} from 'react-router';

import {appRoute} from '../../../../../app-route';
import {useDomainConfig} from '../../../../../provider/domain-config/domain-config-hook';
import {Locale} from '../../../../../provider/locale/localization';
import {useCatalogsByAccount} from '../../../../../service/source-settings/catalogs/catalogs-hook';
import {useStoreAccountsCredentialsBanner} from '../../../../../service/source-settings/store-accounts/store-accounts-hook';
import {sharedSearchParameters} from '../../../../../shared-search-parameters';
import {objectToUrlParameters} from '../../../../../util/url';
import {useUrl} from '../../../../../util/url-hook/url-hook';
import {APP_STORE_CATALOG_ID} from '../../../source-settings/pages/source-links-page/source-content/accounts-table/accounts-table-const';

import * as styles from './appstore-credentials-update-modal.scss';

const openAddAccountQueryKey = 'openAddAccount';

type AppstoreAddAccountQueryType = Record<typeof openAddAccountQueryKey, string>;

export function AppstoreCredentialsUpdateModal(): JSX.Element | null {
    const {companyName} = useDomainConfig();
    const {queries} = useUrl<AppstoreAddAccountQueryType>();
    const {data: bannerData} = useStoreAccountsCredentialsBanner({
        catalogId: APP_STORE_CATALOG_ID,
    });
    const {result: accountCatalogGroups, isInProgress: isCatalogsInProgress} =
        useCatalogsByAccount(APP_STORE_CATALOG_ID);

    const bannerItem = bannerData?.items.find((item) => item.catalogId === APP_STORE_CATALOG_ID);
    const shouldShowCredentialsBanner = Boolean(bannerData?.needToShowBanner && bannerItem?.needsCredentialsBinding);
    const [isDismissed, setIsDismissed] = useState(false);
    const isOpen = shouldShowCredentialsBanner && !isDismissed;

    const settingsPath = useMemo(() => {
        const catalogGroup = accountCatalogGroups?.find((group) =>
            group.catalogs.some((catalog) => catalog.id === APP_STORE_CATALOG_ID)
        );

        if (!catalogGroup) {
            return null;
        }

        return generatePath(appRoute.settingsSourceSettingsAccount.path, {
            groupId: catalogGroup.id,
            id: APP_STORE_CATALOG_ID,
        });
    }, [accountCatalogGroups]);

    const settingsUrlWithQuery = useMemo((): string | null => {
        if (!settingsPath) {
            return null;
        }

        const sharedQueries = Object.fromEntries(
            Object.entries(queries).filter(([key]) => sharedSearchParameters.includes(key))
        );
        const resultQueryMap = {...sharedQueries, [openAddAccountQueryKey]: 'true'};
        const search = objectToUrlParameters(resultQueryMap);

        return search ? `${settingsPath}?${search}` : settingsPath;
    }, [queries, settingsPath]);

    function handleGoToSettings(): void {
        if (!settingsUrlWithQuery) {
            return;
        }

        window.open(settingsUrlWithQuery, '_blank', 'noopener,noreferrer');
    }

    if (!isOpen) {
        return null;
    }

    return (
        <Modal
            destroyOnClose
            focusTriggerAfterClose={false}
            footer={[
                <Button
                    disabled={!settingsUrlWithQuery}
                    key="settings"
                    loading={isCatalogsInProgress}
                    onClick={(event) => {
                        event.stopPropagation();
                        handleGoToSettings();
                    }}
                    type="primary"
                >
                    <Locale stringKey="SOURCE_SETTINGS__ACCOUNTS__APPSTORE__CREDENTIALS_MODAL__BUTTON" />
                </Button>,
            ]}
            maskClosable={false}
            onCancel={() => setIsDismissed(true)}
            open={isOpen}
            title={
                <div className={styles.AppstoreCredentialsUpdateModal_title}>
                    <Locale stringKey="SOURCE_SETTINGS__ACCOUNTS__APPSTORE__CREDENTIALS_MODAL__TITLE" />
                </div>
            }
            width={520}
        >
            <p className={styles.AppstoreCredentialsUpdateModal_body}>
                <Locale
                    stringKey="SOURCE_SETTINGS__ACCOUNTS__APPSTORE__CREDENTIALS_MODAL__BODY"
                    valueMap={{
                        companyName,
                        settingsLink: (
                            <a href={settingsUrlWithQuery ?? '#'} key="settingsLink" rel="noreferrer" target="_blank">
                                <Locale stringKey="SOURCE_SETTINGS__ACCOUNTS__APPSTORE__CREDENTIALS_MODAL__SETTINGS_LINK" />
                            </a>
                        ),
                    }}
                />
            </p>
            <Alert
                className={styles.AppstoreCredentialsUpdateModal_alert}
                message={
                    <span className={styles.AppstoreCredentialsUpdateModal_alertMessage}>
                        <Locale stringKey="SOURCE_SETTINGS__ACCOUNTS__APPSTORE__CREDENTIALS_MODAL__ALERT" />
                    </span>
                }
                showIcon
                type="warning"
            />
        </Modal>
    );
}
